import { HistoricalService } from './HistoricalService';
import { LiveBmsService } from './LiveBmsService';
import { getPreferredDataSourceMode } from './DataSourcePreference';
import { EnergyPlusTestbedService } from './EnergyPlusTestbedService';

export type TwinDataSource =
  | 'historical-default'
  | 'historical-playback'
  | 'live-measured'
  | 'energyplus'
  | 'derived'
  | 'estimated'
  | 'simulation'
  | 'unavailable';

export interface TwinFrame {
  source: TwinDataSource;
  date: string;
  time: string;
  timestamp: number | null;
  pointCount: number;
  snapshot: Record<string, number>;
}

export interface TwinPointReading {
  pointId: string;
  value: number | null;
  source: TwinDataSource;
  timestamp: number | null;
  available: boolean;
}

type Listener = (frame: TwinFrame) => void;

const DEFAULT_DATE = '20180831';
const DEFAULT_TIME = '14:00:00';

const emptyFrame = (): TwinFrame => ({
  source: 'unavailable',
  date: '',
  time: '',
  timestamp: null,
  pointCount: 0,
  snapshot: {},
});

const formatDate = (date: string) =>
  date.length === 8 ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}` : date;

const frameTimestamp = (date: string, time: string) => {
  if (!date || !time) return null;
  const value = new Date(`${formatDate(date)}T${time}+09:00`).getTime();
  return Number.isFinite(value) ? value : null;
};

class _TwinContextService {
  private frame: TwinFrame = emptyFrame();
  private listeners = new Set<Listener>();
  private initialized = false;
  private initializePromise: Promise<void> | null = null;

  private applyPreferredSource = async () => {
    const preferred = getPreferredDataSourceMode();
    if (preferred === 'energyplus') {
      if (HistoricalService.isActive()) HistoricalService.stop();
      EnergyPlusTestbedService.start();
      if (!this.applyEnergyPlusFrame(EnergyPlusTestbedService.getStatus().frame)) {
        this.setFrame(emptyFrame());
      }
      return;
    }
    if (preferred === 'live') {
      if (HistoricalService.isActive()) HistoricalService.stop();
      await LiveBmsService.refresh();
      if (!this.applyLiveFrame()) this.setFrame(emptyFrame());
      return;
    }
    if (HistoricalService.isActive()) {
      this.applyHistoricalPlayback();
      return;
    }
    await this.loadDefaultHistoricalFrame();
  };

  initialize(): Promise<void> {
    if (this.initializePromise) return this.initializePromise;
    this.initializePromise = this.start();
    return this.initializePromise;
  }

  private async start() {
    if (this.initialized) return;
    this.initialized = true;
    window.addEventListener('aees:data-source-preference', () => {
      void this.applyPreferredSource();
    });
    await HistoricalService.restore();
    HistoricalService.subscribe(status => {
      const preferred = getPreferredDataSourceMode();
      if (preferred === 'energyplus') {
        EnergyPlusTestbedService.start();
        if (!this.applyEnergyPlusFrame(EnergyPlusTestbedService.getStatus().frame)) {
          this.setFrame(emptyFrame());
        }
        return;
      }
      if (preferred === 'live') {
        void LiveBmsService.refresh().then(() => {
          if (!this.applyLiveFrame()) this.setFrame(emptyFrame());
        });
        return;
      }
      if (status.state !== 'stopped') {
        this.applyHistoricalPlayback();
        return;
      }
      void this.loadDefaultHistoricalFrame();
    });
    LiveBmsService.subscribe(() => {
      if (getPreferredDataSourceMode() === 'live' && !HistoricalService.isActive()) {
        this.applyLiveFrame();
      }
    });
    EnergyPlusTestbedService.subscribe(status => {
      if (getPreferredDataSourceMode() === 'energyplus' && !HistoricalService.isActive()) {
        if (!this.applyEnergyPlusFrame(status.frame)) {
          this.setFrame(emptyFrame());
        }
      }
    });
    const preferred = getPreferredDataSourceMode();
    LiveBmsService.start();

    if (preferred === 'energyplus') {
      if (HistoricalService.isActive()) HistoricalService.stop();
      EnergyPlusTestbedService.start();
      if (!this.applyEnergyPlusFrame(EnergyPlusTestbedService.getStatus().frame)) {
        this.setFrame(emptyFrame());
      }
    } else if (preferred === 'live') {
      if (HistoricalService.isActive()) HistoricalService.stop();
      await LiveBmsService.refresh();
      if (!this.applyLiveFrame()) this.setFrame(emptyFrame());
    } else if (HistoricalService.isActive()) {
      this.applyHistoricalPlayback();
    } else {
      await this.loadDefaultHistoricalFrame();
    }
  }

  getFrame(): TwinFrame {
    return { ...this.frame, snapshot: { ...this.frame.snapshot } };
  }

  getReading(pointId: string): TwinPointReading {
    const value = this.frame.snapshot[pointId];
    const available = Number.isFinite(value);
    return {
      pointId,
      value: available ? value : null,
      source: available ? this.frame.source : 'unavailable',
      timestamp: this.frame.timestamp,
      available,
    };
  }

  getReadings(pointIds: string[]): Record<string, TwinPointReading> {
    return Object.fromEntries(pointIds.map(pointId => [pointId, this.getReading(pointId)]));
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    void this.initialize().then(async () => {
      if (HistoricalService.isActive()) {
        this.applyHistoricalPlayback();
      } else if (this.frame.source === 'unavailable') {
        if (getPreferredDataSourceMode() === 'energyplus') {
          EnergyPlusTestbedService.start();
          this.applyEnergyPlusFrame(EnergyPlusTestbedService.getStatus().frame);
        } else if (getPreferredDataSourceMode() === 'live') {
          await LiveBmsService.refresh();
          this.applyLiveFrame();
        } else {
          await this.loadDefaultHistoricalFrame();
        }
      }
      listener(this.getFrame());
    });
    return () => this.listeners.delete(listener);
  }

  async loadDefaultHistoricalFrame(date = DEFAULT_DATE, time = DEFAULT_TIME) {
    if (getPreferredDataSourceMode() === 'energyplus') {
      EnergyPlusTestbedService.start();
      if (!this.applyEnergyPlusFrame(EnergyPlusTestbedService.getStatus().frame)) {
        this.setFrame(emptyFrame());
      }
      return;
    }
    if (getPreferredDataSourceMode() === 'live') {
      await LiveBmsService.refresh();
      if (!this.applyLiveFrame()) this.setFrame(emptyFrame());
      return;
    }
    if (HistoricalService.isActive()) {
      this.applyHistoricalPlayback();
      return;
    }
    try {
      const response = await fetch(`/api/historical/snapshot?date=${date}&time=${time}`);
      if (!response.ok) throw new Error('Historical snapshot unavailable');
      const payload = await response.json();
      const snapshot: Record<string, number> = payload.data ?? {};
      if (HistoricalService.isActive()) {
        this.applyHistoricalPlayback();
        return;
      }
      this.setFrame({
        source: 'historical-default',
        date: formatDate(date),
        time,
        timestamp: frameTimestamp(date, time),
        pointCount: payload.pointCount ?? Object.keys(snapshot).length,
        snapshot,
      });
    } catch {
      if (!HistoricalService.isActive()) this.setFrame(emptyFrame());
    }
  }

  private applyHistoricalPlayback() {
    const status = HistoricalService.getStatus();
    if (status.state === 'stopped') return;
    const time = new Date(status.seconds * 1000).toISOString().slice(11, 19);
    this.setFrame({
      source: 'historical-playback',
      date: formatDate(status.date),
      time,
      timestamp: frameTimestamp(status.date, time),
      pointCount: status.pointCount,
      snapshot: HistoricalService.getSnapshot(),
    });
  }

  private applyLiveFrame(): boolean {
    const status = LiveBmsService.getStatus();
    if (!status.connected || status.pointCount === 0) return false;

    const snapshot = LiveBmsService.getSnapshot();
    const timestamp = status.lastUpdate ?? Date.now();
    const kst = new Date(timestamp + (9 * 3600 * 1000));
    const y = kst.getUTCFullYear();
    const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
    const d = String(kst.getUTCDate()).padStart(2, '0');
    const hh = String(kst.getUTCHours()).padStart(2, '0');
    const mm = String(kst.getUTCMinutes()).padStart(2, '0');
    const ss = String(kst.getUTCSeconds()).padStart(2, '0');

    this.setFrame({
      source: 'live-measured',
      date: `${y}-${m}-${d}`,
      time: `${hh}:${mm}:${ss}`,
      timestamp,
      pointCount: status.pointCount,
      snapshot,
    });
    return true;
  }

  private applyEnergyPlusFrame(frame: ReturnType<typeof EnergyPlusTestbedService.getStatus>['frame']): boolean {
    if (!frame || frame.pointCount === 0) return false;
    this.setFrame({
      source: 'energyplus',
      date: frame.date,
      time: frame.time,
      timestamp: frame.timestamp,
      pointCount: frame.pointCount,
      snapshot: frame.snapshot,
    });
    return true;
  }

  private setFrame(frame: TwinFrame) {
    this.frame = frame;
    const copy = this.getFrame();
    this.listeners.forEach(listener => listener(copy));
  }
}

export const TwinContextService = new _TwinContextService();
